// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {currentState} from '@github-ui/history'
import type {Location, Navigator, To} from '@github-ui/react-router'
import {createPath, Router, useInRouterContext} from '@github-ui/react-router'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {type PropsWithChildren, useCallback, useEffect, useState} from 'react'

/**
 * Get the current location from the browser URL.
 * This is used to initialize and update the router state.
 *
 * Reads state and key from window.history.state to match BrowserRouter behavior.
 * React Router stores these as { usr: state, key: string, idx: number }.
 */
function getLocationFromBrowser(): Location {
  const {pathname, search, hash} = ssrSafeLocation
  const historyState = currentState()
  return {
    pathname: pathname || '/',
    search: search || '',
    hash: hash || '',
    state: historyState.usr || null,
    key: historyState.key || 'default',
  }
}

/**
 * A read-only navigator that delegates all navigation to Turbo/the main page.
 * This prevents the partial's router from modifying browser history directly.
 */
function createReadOnlyNavigator(): Navigator {
  return {
    createHref(to: To): string {
      return typeof to === 'string' ? to : createPath(to)
    },
    go(_delta: number): void {
      // No-op: partials should not control browser history
      // Navigation should be handled by Turbo or the parent page
    },
    push(_to: To, _state?: unknown, _opts?: {preventScrollReset?: boolean; relative?: 'route' | 'path'}): void {
      // No-op: partials should not control browser history
    },
    replace(_to: To, _state?: unknown, _opts?: {preventScrollReset?: boolean; relative?: 'route' | 'path'}): void {
      // No-op: partials should not control browser history
    },
  }
}

// Singleton navigator instance since it's stateless
const readOnlyNavigator = createReadOnlyNavigator()

/**
 * A router component for React partials that provides routing context
 * without controlling browser history.
 *
 * This component:
 * - Provides router context so hooks like useLocation, useSearchParams work
 * - Reads the current URL from the browser (stays in sync via popstate events)
 * - Does NOT push/replace to browser history (navigation is handled by Turbo)
 * - Is nested-router-safe: if already inside a Router, it just renders children
 *
 * This allows partials to:
 * - Use React Router hooks to read the current URL
 * - Render content based on URL parameters
 * - Not interfere with the main page's navigation handling
 */
export function PartialRouter({children, partialName}: PropsWithChildren<{partialName: string}>) {
  // Check if we're already inside a Router context
  // This avoids "nested router" errors when a partial is rendered inside an existing router
  const isInsideRouter = useInRouterContext()

  // we can remove this later - I'm curious if we can see where this was happening
  useEffect(() => {
    if (isInsideRouter) {
      reportError(new Error(`PartialRouter used inside another Router in partial "${partialName}"`))
    }
  }, [isInsideRouter, partialName])

  // If we're already inside a router, just render children without adding another Router
  if (isInsideRouter) {
    return <>{children}</>
  }

  return <PartialRouterInner>{children}</PartialRouterInner>
}

/**
 * Internal component that provides the actual Router.
 * Separated to avoid hooks being called conditionally in PartialRouter.
 */
function PartialRouterInner({children}: PropsWithChildren) {
  const [location, setLocation] = useState(getLocationFromBrowser)

  // Sync location state when the browser URL changes (e.g., via Turbo navigation, back/forward)
  const handleLocationChange = useCallback(() => {
    setLocation(getLocationFromBrowser())
  }, [])

  useEffect(() => {
    // Listen for popstate events (browser back/forward, Turbo navigation)
    window.addEventListener('popstate', handleLocationChange)

    // Also listen to turbo:load for Turbo-driven navigations
    document.addEventListener('turbo:load', handleLocationChange)

    return () => {
      window.removeEventListener('popstate', handleLocationChange)
      document.removeEventListener('turbo:load', handleLocationChange)
    }
  }, [handleLocationChange])

  return (
    <Router location={location} navigator={readOnlyNavigator}>
      {children}
    </Router>
  )
}

try{ PartialRouter.displayName ||= 'PartialRouter' } catch {}
try{ PartialRouterInner.displayName ||= 'PartialRouterInner' } catch {}